import {matchPath} from "react-router-dom";

// Каноничната карта URL → пътечка (breadcrumb) + локални табове.
// Всичко е URL-driven (§4 от G2_NAVIGATION_DESIGN.md): SubNav и TopNav не се
// регистрират от страниците, а извеждат контекста от pathname-а.

const truncate = (s, n = 25) =>
    s && s.length > n ? `${s.slice(0, n)}…` : s;

// Щ2: крошка за ОБЩИНАТА, между „Землища" и самото землище.
//
// Списъкът със землища е хармоника по община и без тази стъпка връщането
// назад води до всички общини, тоест човек всеки път търси наново своята.
// Затова връзката носи общината в адреса (`?m=`), а списъкът я отваря.
function municipalityCrumb(year, settlement) {
    if (!settlement || !settlement.municipality) return null;
    return {
        label: `общ. ${settlement.municipality}`,
        to: `/${year}/settlements?m=${encodeURIComponent(settlement.municipality)}`,
    };
}

// Имена на справките по route — единен формат „СN — Име" (Е3). Номерацията
// съответства на екранните заглавия (преномерирането чака Е7).
const REPORT_NAMES = {
    s1: 'С1 — Справка на землище',
    s2: 'С2 — Справка по ползвател за всички землища',
    s3: 'С10 — ДЗ кръстосана по ползвател',
    s4: 'С7 — ДЗ между двама ползватели',
    s5: 'С3 — Пълна справка по ползвател',
    s4general: 'С4 — Обща справка по ползвател',
    s8: 'С8 — Справка по ползвател с корекция',
    control: 'С9 — Контролна справка на землище',
    cross: 'С5 — ДЗ кръстосана',
    protocols: 'С6 — ДЗ-протоколи',
    crops: 'С11 — Обобщени култури по ползвател',
    s13: 'С13 — БП и пътища',
    s14: 'С14 — Изчертани площи, текуща и предходна година',
};

// Връща годината от URL-а (или null, ако няма контекст с година).
export function getYearFromPath(pathname) {
    const info = matchPath('/info/:year/:settlement_ekatte/:bullstat/*', pathname);
    if (info) return info.params.year;
    const y = matchPath('/:year/*', pathname);
    if (y && /^\d+$/.test(y.params.year)) return y.params.year;
    return null;
}

// Активната секция за подчертаване на Ред 1.
export function getSection(pathname) {
    if (matchPath('/info/:year/:settlement_ekatte/:bullstat/*', pathname)) return 'settlements';
    const y = matchPath('/:year/*', pathname);
    if (y && /^\d+$/.test(y.params.year)) {
        const rest = y.params['*'] || '';
        if (rest.startsWith('settlements')) return 'settlements';
        if (rest.startsWith('companies')) return 'companies';
        if (rest.startsWith('reports')) return 'reports';
    }
    if (matchPath('/data-upload', pathname)) return 'upload';
    if (matchPath('/help', pathname)) return 'help';
    if (pathname === '/admin' || matchPath('/admin/*', pathname)) return 'admin';
    if (pathname === '/') return 'home';
    return null;
}

// Пътечката (breadcrumb) за текущия pathname. `to === null` → текуща стъпка (без линк).
export function buildCrumbs(pathname, {settlements = [], companies = []} = {}) {
    const crumbs = [];

    // Админ панел: Администрация › <раздел>. Разделите са в лявата колона на
    // AdminLayout, затова тук само пътечка (без локални табове).
    if (pathname === '/admin' || matchPath('/admin/*', pathname)) {
        const sub = matchPath('/admin/users', pathname) ? 'Потребители'
            : matchPath('/admin/data', pathname) ? 'Данни'
            : matchPath('/admin/nomenclature', pathname) ? 'Номенклатури'
            : matchPath('/admin/activity', pathname) ? 'Активност'
            : matchPath('/admin/system', pathname) ? 'Система' : null;
        crumbs.push({label: 'Администрация', to: sub ? '/admin' : null});
        if (sub) crumbs.push({label: sub, to: null});
        return crumbs;
    }

    // Страница на ползвател: /info/:year/:ekatte/:bullstat/*
    const info = matchPath('/info/:year/:settlement_ekatte/:bullstat/*', pathname);
    if (info) {
        const {year, settlement_ekatte, bullstat} = info.params;
        const settlement = settlements.find(s => s.ekatte === settlement_ekatte);
        const company = companies.find(c => String(c.bullstat) === String(bullstat));
        crumbs.push({label: year, to: '/'});
        crumbs.push({label: 'Землища', to: `/${year}/settlements`});
        const общинаНаФирма = municipalityCrumb(year, settlement);
        if (общинаНаФирма) crumbs.push(общинаНаФирма);
        crumbs.push({
            label: settlement ? settlement.settlement : settlement_ekatte,
            to: `/${year}/settlements/${settlement_ekatte}/companies`,
        });
        crumbs.push({label: company ? truncate(company.company_name) : bullstat, to: null});
        return crumbs;
    }

    const y = matchPath('/:year/*', pathname);
    if (y && /^\d+$/.test(y.params.year)) {
        const year = y.params.year;
        const rest = y.params['*'] || '';
        crumbs.push({label: year, to: '/'});

        // Контекст землище: /:year/settlements/:ekatte/*
        const sett = matchPath('/:year/settlements/:settlement_ekatte/*', pathname);
        if (sett) {
            const {settlement_ekatte} = sett.params;
            const settlement = settlements.find(s => s.ekatte === settlement_ekatte);
            crumbs.push({label: 'Землища', to: `/${year}/settlements`});
            const община = municipalityCrumb(year, settlement);
            if (община) crumbs.push(община);
            crumbs.push({
                label: settlement ? settlement.settlement : settlement_ekatte,
                to: null,
            });
            return crumbs;
        }
        if (rest.startsWith('settlements')) {
            crumbs.push({label: 'Землища', to: null});
            return crumbs;
        }
        if (rest.startsWith('companies')) {
            crumbs.push({label: 'Ползватели-редакция', to: null});
            return crumbs;
        }
        if (rest.startsWith('reports')) {
            crumbs.push({label: 'Справки', to: `/${year}/reports`});
            const rep = matchPath('/:year/reports/:report/*', pathname);
            if (rep && REPORT_NAMES[rep.params.report]) {
                crumbs.push({label: REPORT_NAMES[rep.params.report], to: null});
            }
            return crumbs;
        }
        return crumbs;
    }

    if (matchPath('/data-upload', pathname)) return [{label: 'Първоначални данни', to: null}];
    if (matchPath('/help', pathname)) return [{label: 'Помощ', to: null}];

    return crumbs;
}

// Локалните табове за текущия контекст. Всеки таб: {key, label, to}.
// `key` служи за динамични маркери (В3 през NavContext).
export function buildTabs(pathname) {
    // Контекст ползвател — подсекциите на страницата на ползвателя.
    const info = matchPath('/info/:year/:settlement_ekatte/:bullstat/*', pathname);
    if (info) {
        const {year, settlement_ekatte, bullstat} = info.params;
        const base = `/info/${year}/${settlement_ekatte}/${bullstat}`;
        return [
            // Н4: редът следва реалната работа — ДЗ, после изчертаването, после
            // белите петна и пътищата, и накрая другите площи.
            {key: 'company.deal', label: 'ДЗ', to: `${base}/deal`},
            {key: 'company.drawing', label: 'Изчертаване', to: `${base}/drawing`},
            {key: 'company.bproads', label: 'БП и пътища', to: `${base}/bp-roads`},
            {key: 'company.other', label: 'Други площи', to: `${base}/other`},
            {key: 'company.company', label: 'Данни ползвател', to: `${base}/company`},
            {key: 'company.copy', label: 'Копиране', to: `${base}/copy`},
            // Е13: справките по номер (С3→С4→С9), с кратки имена без номер (за да
            // се събере целият ред табове на един ред). Изключение от формата „СN — Име".
            {key: 'company.s3', label: 'Пълна справка', to: `/${year}/reports/s5/${settlement_ekatte}/${bullstat}`},
            {key: 'company.s4', label: 'Обща справка', to: `/${year}/reports/s4general/${settlement_ekatte}/${bullstat}`},
            {key: 'company.s9', label: 'Контролна справка', to: `/${year}/reports/control/${settlement_ekatte}`},
        ];
    }

    // Контекст землище — локалните табове на землището.
    const sett = matchPath('/:year/settlements/:settlement_ekatte/*', pathname);
    if (sett) {
        const {year, settlement_ekatte} = sett.params;
        return [
            {key: 'settlement.companies', label: 'Ползватели', to: `/${year}/settlements/${settlement_ekatte}/companies`},
            {key: 'settlement.deals', label: 'ДЗ', to: `/${year}/settlements/${settlement_ekatte}/deals`},
            {key: 'settlement.s1', label: 'С1 — Справка на землище', to: `/${year}/reports/s1/${settlement_ekatte}`},
            {key: 'settlement.s13', label: 'БП и пътища', to: `/${year}/reports/s13/${settlement_ekatte}`},
            {key: 'settlement.compare', label: 'Сравнение на декларации', to: `/${year}/settlements/${settlement_ekatte}/compare`},
        ];
    }

    return [];
}
